<script setup>
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { ref, computed, onMounted } from 'vue';

import PageHeader from '../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SubServiceModal from './SubServiceModal.vue';
import TeamsAPI from 'dashboard/api/teams';

// Threshold para distinguir IDs temporários (timestamps) de IDs do banco de dados
// IDs temporários são gerados com Date.now() (~13 dígitos), IDs do banco são pequenos
const TEMPORARY_ID_THRESHOLD = 1000000000000; // 1 trilhão

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const getters = useStoreGetters();

const form = ref({
  id: null,
  name: '',
  description: '',
  team_id: '',
  sla_policy_id: '',
  sub_services: [],
});

const errors = ref({});
const isSubmitting = ref(false);
const showSubServiceModal = ref(false);
const editingSubService = ref(null);
const isLoading = ref(false);

const teamsFromStore = computed(() => getters['teams/getTeams'].value);
const teamsWithBoard = ref([]);
const teams = computed(() => {
  // Use teams with has_board if available, otherwise fallback to store teams
  return teamsWithBoard.value.length > 0
    ? teamsWithBoard.value
    : teamsFromStore.value;
});
const slas = computed(() => getters['sla/getSLA'].value);

const isEditing = computed(() => !!route.params.id);
const isServiceSaved = computed(
  () => !!form.value.id && typeof form.value.id === 'number'
);

const truncateTeamName = name => {
  return name.length > 25 ? `${name.slice(0, 25)}...` : name;
};

const loadService = async () => {
  isLoading.value = true;
  try {
    const decimalBase = 10;
    const serviceId = parseInt(route.params.id, decimalBase);
    await store.dispatch('services/show', { id: serviceId });
    // Acessa o serviço diretamente do store após o dispatch
    const service = store.getters['services/getServiceById'](serviceId);

    if (service && service.id) {
      form.value = {
        id: service.id,
        name: service.name || '',
        description: service.description || '',
        team_id: service.team_id || '',
        sla_policy_id: service.sla_policy_id || '',
        sub_services: (service.sub_services || []).map(sub => ({
          id: sub.id,
          name: sub.name || '',
          description: sub.description || '',
          sla_policy_id: sub.sla_policy_id || '',
        })),
      };
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      t('SERVICES_SETTINGS.EDIT.ERROR');
    useAlert(errorMessage);
    router.push({ name: 'settings_services_list' });
  } finally {
    isLoading.value = false;
  }
};

const selectedTeam = computed(() => {
  if (!form.value.team_id) return null;
  return teams.value.find(team => team.id === Number(form.value.team_id));
});

const teamHasBoard = computed(() => {
  return selectedTeam.value?.has_board ?? true;
});

const fetchTeamsWithBoard = async () => {
  try {
    // Fetch teams directly from network to get has_board field
    const response = await TeamsAPI.get(false);
    const teamsData = TeamsAPI.extractDataFromResponse(response);
    teamsWithBoard.value = teamsData;
  } catch (error) {
    await store.dispatch('teams/get');
  }
};

onMounted(async () => {
  // Fetch teams with has_board info directly
  await fetchTeamsWithBoard();
  await store.dispatch('sla/get');

  if (isEditing.value) {
    await loadService();
  }
});

const validate = () => {
  errors.value = {};
  let isValid = true;

  if (!form.value.name.trim()) {
    errors.value.name = t('SERVICES_SETTINGS.FORM.NAME.ERROR');
    isValid = false;
  }

  if (!form.value.description.trim()) {
    errors.value.description = t('SERVICES_SETTINGS.FORM.DESCRIPTION.ERROR');
    isValid = false;
  }

  if (!form.value.team_id) {
    errors.value.team_id = t('SERVICES_SETTINGS.FORM.TEAM.ERROR');
    isValid = false;
  }

  // SLA só é obrigatório na criação, não na edição
  if (!isEditing.value && !form.value.sla_policy_id) {
    errors.value.sla_policy_id = t('SERVICES_SETTINGS.FORM.SLA.ERROR');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validate()) {
    return;
  }

  isSubmitting.value = true;
  try {
    const payload = {
      name: form.value.name,
      description: form.value.description,
      team_id: form.value.team_id || null,
      sla_policy_id: form.value.sla_policy_id || null,
      sub_services: form.value.sub_services.map(sub => {
        // Só envia ID se for um ID real do banco (não temporário)
        const isRealId =
          sub.id &&
          typeof sub.id === 'number' &&
          sub.id < TEMPORARY_ID_THRESHOLD;
        return {
          id: isRealId ? sub.id : undefined,
          name: sub.name,
          description: sub.description || null,
          sla_policy_id: sub.sla_policy_id || null,
        };
      }),
    };

    if (isEditing.value) {
      await store.dispatch('services/update', {
        id: form.value.id,
        ...payload,
      });
      useAlert(t('SERVICES_SETTINGS.EDIT.SUCCESS'));
    } else {
      await store.dispatch('services/create', payload);
      useAlert(t('SERVICES_SETTINGS.CREATE.SUCCESS'));
    }

    // Recarrega a lista de serviços
    await store.dispatch('services/get');

    router.push({ name: 'settings_services_list' });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      (isEditing.value
        ? t('SERVICES_SETTINGS.EDIT.ERROR')
        : t('SERVICES_SETTINGS.CREATE.ERROR'));
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const openSubServiceModal = (subService = null) => {
  // Se receber um evento ao invés de um objeto, trata como null
  if (
    subService &&
    (subService instanceof Event || subService instanceof PointerEvent)
  ) {
    subService = null;
  }
  editingSubService.value = subService;
  showSubServiceModal.value = true;
};

const closeSubServiceModal = () => {
  showSubServiceModal.value = false;
  editingSubService.value = null;
};

const saveSubService = subServiceData => {
  // Verifica se está editando (tem ID e não é um evento)
  const isEditingSubService =
    editingSubService.value &&
    !(editingSubService.value instanceof Event) &&
    !(editingSubService.value instanceof PointerEvent) &&
    editingSubService.value.id;

  if (isEditingSubService) {
    const index = form.value.sub_services.findIndex(
      s => s.id === editingSubService.value.id
    );
    if (index !== -1) {
      form.value.sub_services[index] = { ...subServiceData };
    }
  } else {
    // Remove o id temporário se existir (vem do modal)
    const { id, ...subServiceDataWithoutId } = subServiceData;
    form.value.sub_services.push({
      ...subServiceDataWithoutId,
      id: Date.now(), // Temporary ID
    });
  }

  closeSubServiceModal();
};

const isSubServiceSaved = subService => {
  if (!subService || !subService.id) return false;

  return (
    typeof subService.id === 'number' && subService.id < TEMPORARY_ID_THRESHOLD
  );
};

const deleteSubService = async subServiceId => {
  // Se o subserviço tem ID do backend, precisa validar com a API
  const subService = form.value.sub_services.find(s => s.id === subServiceId);
  if (subService && subService.id && typeof subService.id === 'number') {
    try {
      // Se já foi salvo no backend, validar antes de deletar
      // Por enquanto, apenas remove do array local
      // A validação será feita quando salvar o serviço
      form.value.sub_services = form.value.sub_services.filter(
        s => s.id !== subServiceId
      );
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        t('SERVICES_SETTINGS.SUBSERVICES.DELETE_ERROR');
      useAlert(errorMessage);
    }
  } else {
    // Se é apenas local (não salvo ainda), pode remover diretamente
    form.value.sub_services = form.value.sub_services.filter(
      s => s.id !== subServiceId
    );
  }
};

const editSubService = subService => {
  openSubServiceModal(subService);
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <woot-loading-state
      v-if="isLoading"
      :message="$t('SERVICES_SETTINGS.LOADING')"
    />
    <PageHeader
      v-else
      :header-title="
        isEditing
          ? $t('SERVICES_SETTINGS.EDIT.TITLE')
          : $t('SERVICES_SETTINGS.CREATE.TITLE')
      "
      :header-content="
        isEditing
          ? $t('SERVICES_SETTINGS.EDIT.DESCRIPTION')
          : $t('SERVICES_SETTINGS.CREATE.DESCRIPTION')
      "
    />
    <form
      v-if="!isLoading"
      class="flex flex-col gap-6 mt-6"
      @submit.prevent="handleSubmit"
    >
      <div class="grid grid-cols-1 gap-6">
        <Input
          v-model="form.name"
          :label="$t('SERVICES_SETTINGS.FORM.NAME.LABEL')"
          :placeholder="$t('SERVICES_SETTINGS.FORM.NAME.PLACEHOLDER')"
          :message="errors.name"
          :message-type="errors.name ? 'error' : 'info'"
          required
        />

        <TextArea
          v-model="form.description"
          :label="$t('SERVICES_SETTINGS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('SERVICES_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
          :message="errors.description"
          :message-type="errors.description ? 'error' : 'info'"
          resize
          required
        />

        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ $t('SERVICES_SETTINGS.FORM.TEAM.LABEL') }}
            </label>
            <select
              v-model="form.team_id"
              class="block w-full reset-base text-sm h-10 !mb-0 outline outline-1 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 border-none border-0 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
              :class="{
                'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9':
                  errors.team_id,
                'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
                  !errors.team_id,
              }"
            >
              <option value="" disabled>
                {{ $t('SERVICES_SETTINGS.FORM.TEAM.PLACEHOLDER') }}
              </option>
              <option v-for="team in teams" :key="team.id" :value="team.id">
                {{ truncateTeamName(team.name) }}
              </option>
            </select>
            <p
              v-if="errors.team_id"
              class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
            >
              {{ errors.team_id }}
            </p>
            <p
              v-if="form.team_id && !teamHasBoard"
              class="min-w-0 mt-1 mb-0 text-xs transition-all duration-500 ease-in-out text-n-slate-10 dark:text-n-slate-10"
            >
              {{ $t('SERVICES_SETTINGS.FORM.TEAM.NO_BOARD_MESSAGE') }}
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ $t('SERVICES_SETTINGS.FORM.SLA.LABEL') }}
            </label>
            <select
              v-model="form.sla_policy_id"
              :disabled="isServiceSaved"
              class="block w-full reset-base text-sm h-10 !mb-0 outline outline-1 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 border-none border-0 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
              :class="{
                'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9':
                  errors.sla_policy_id,
                'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
                  !errors.sla_policy_id,
              }"
            >
              <option value="" disabled>
                {{ $t('SERVICES_SETTINGS.FORM.SLA.PLACEHOLDER') }}
              </option>
              <option v-for="sla in slas" :key="sla.id" :value="sla.id">
                {{ sla.name }}
              </option>
            </select>
            <p
              v-if="errors.sla_policy_id"
              class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
            >
              {{ errors.sla_policy_id }}
            </p>
            <p
              v-if="isServiceSaved"
              class="min-w-0 mt-1 mb-0 text-xs text-n-slate-11"
            >
              {{ $t('SERVICES_SETTINGS.FORM.SLA.EDIT_DISABLED') }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4 mt-4">
        <div class="flex items-center justify-between">
          <div class="flex flex-col gap-1">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('SERVICES_SETTINGS.SUBSERVICES.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ $t('SERVICES_SETTINGS.SUBSERVICES.DESCRIPTION') }}
            </p>
          </div>
          <Button
            type="button"
            icon="i-lucide-circle-plus"
            :label="$t('SERVICES_SETTINGS.SUBSERVICES.ADD')"
            @click="() => openSubServiceModal()"
          />
        </div>

        <div
          v-if="form.sub_services.length === 0"
          class="flex items-center justify-center p-8 bg-n-alpha-black2 rounded-lg border border-n-weak"
        >
          <p class="text-sm text-n-slate-11">
            {{ $t('SERVICES_SETTINGS.SUBSERVICES.EMPTY') }}
          </p>
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="subService in form.sub_services"
            :key="subService.id"
            class="flex items-center justify-between p-4 bg-n-alpha-black2 rounded-lg border border-n-weak"
          >
            <div class="flex flex-col gap-1">
              <span class="font-medium text-n-slate-12">{{
                subService.name
              }}</span>
              <p v-if="subService.description" class="text-sm text-n-slate-11">
                {{ subService.description }}
              </p>
            </div>
            <div class="flex gap-2">
              <Button
                type="button"
                icon="i-lucide-pencil"
                slate
                xs
                faded
                @click="editSubService(subService)"
              />
              <Button
                type="button"
                icon="i-lucide-trash-2"
                ruby
                xs
                faded
                @click="deleteSubService(subService.id)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <Button
          type="button"
          variant="faded"
          slate
          :label="$t('SERVICES_SETTINGS.FORM.CANCEL')"
          @click="router.push({ name: 'settings_services_list' })"
        />
        <Button
          type="submit"
          :label="
            isEditing
              ? $t('SERVICES_SETTINGS.FORM.SUBMIT_EDIT')
              : $t('SERVICES_SETTINGS.FORM.SUBMIT')
          "
          :is-loading="isSubmitting"
        />
      </div>
    </form>

    <SubServiceModal
      v-model:show="showSubServiceModal"
      :sub-service="editingSubService"
      :slas="slas"
      :is-sub-service-saved="
        editingSubService ? isSubServiceSaved(editingSubService) : false
      "
      @save="saveSubService"
      @close="closeSubServiceModal"
    />
  </div>
</template>
